import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import Navbar from '../components/Navbar';
import LoadingSpinner, { PageLoader } from '../components/LoadingSpinner';
import { toast } from 'sonner';
import { resolveImageUrl } from '../utils/imageUtils';
import { 
  Gavel, Timer, Crown, Play, Plus, Video, Users,
  Flame, Zap, Trophy, Radio, Package, Bolt, Camera, X
} from 'lucide-react';

// Timer intervals configuration
// Sprints: 5s, 10s, 15s, 20s, 25s, 30s, 45s
// Standard: 1m, 2m, 3m, 5m
const TIMER_INTERVALS = [
  { value: '5', label: '5 sec', category: 'sprint' },
  { value: '10', label: '10 sec', category: 'sprint' },
  { value: '15', label: '15 sec', category: 'sprint' },
  { value: '20', label: '20 sec', category: 'sprint' },
  { value: '25', label: '25 sec', category: 'sprint' },
  { value: '30', label: '30 sec', category: 'sprint' },
  { value: '45', label: '45 sec', category: 'sprint' },
  { value: '60', label: '1 min', category: 'standard' },
  { value: '120', label: '2 min', category: 'standard' },
  { value: '180', label: '3 min', category: 'standard' },
  { value: '300', label: '5 min', category: 'standard' },
];

// ==================== AUCTION CARD COMPONENT ====================

const AuctionCard = ({ auction }) => {
  const [timeRemaining, setTimeRemaining] = useState(auction.time_remaining_seconds);

  useEffect(() => {
    if (auction.status !== 'live') return;
    
    const timer = setInterval(() => {
      setTimeRemaining(prev => Math.max(0, prev - 1));
    }, 1000);
    
    return () => clearInterval(timer);
  }, [auction.status]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isUrgent = timeRemaining <= 30;
  const imageUrl = resolveImageUrl(auction.card_details?.image_url);

  return (
    <Link 
      to={`/auction/${auction.id}`}
      className="block bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden hover:border-[#00BFFF]/50 transition-all group"
    >
      {/* Image */}
      <div className="relative aspect-[4/3] bg-zinc-800">
        {imageUrl ? (
          <img src={imageUrl} alt={auction.card_details?.player_name} className="w-full h-full object-contain" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Video className="w-12 h-12 text-zinc-700" />
          </div>
        )}
        
        {/* LIVE Badge */}
        {auction.status === 'live' && (
          <div className="absolute top-2 left-2 flex items-center gap-1.5 px-2 py-1 bg-red-600 rounded-full">
            <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
            <span className="text-white font-bold text-xs">LIVE</span>
          </div>
        )}

        {/* Timer Overlay */}
        {auction.status === 'live' && (
          <div className={`absolute bottom-2 right-2 px-2 py-1 rounded-lg font-mono font-bold ${
            isUrgent ? 'bg-red-600 text-white animate-pulse' : 'bg-black/80 text-white'
          }`}>
            <Timer className="w-3 h-3 inline mr-1" />
            {formatTime(timeRemaining)}
          </div>
        )}

        {/* Ended Badge */}
        {auction.status === 'ended' && (
          <div className="absolute top-2 left-2 px-2 py-1 bg-zinc-700 rounded-full">
            <span className="text-white font-bold text-xs">ENDED</span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4">
        <h3 className="text-white font-bold truncate group-hover:text-[#00BFFF] transition-colors">
          {auction.card_details?.player_name}
        </h3>
        <p className="text-zinc-400 text-sm truncate">
          {auction.card_details?.year} {auction.card_details?.set_name}
        </p>
        
        <div className="flex items-center justify-between mt-3">
          <div>
            <p className="text-zinc-500 text-xs">Current Bid</p>
            <p className="text-emerald-400 font-bold text-lg">${auction.current_price?.toFixed(2)}</p>
          </div>
          <div className="text-right">
            <p className="text-zinc-500 text-xs">{auction.bid_count || auction.entry_count || 0} {auction.is_giveaway ? 'entries' : 'bids'}</p>
            <p className="text-zinc-400 text-sm">by {auction.host_name}</p>
          </div>
        </div>
      </div>
    </Link>
  );
};

// ==================== CREATE AUCTION MODAL ====================

const CreateAuctionModal = ({ onClose, onCreated }) => {
  const { api, user } = useAuth();
  const [userListings, setUserListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [auctionMode, setAuctionMode] = useState('regular'); // 'regular' or 'quicklist'
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  const [formData, setFormData] = useState({
    card_id: '',
    starting_price: '1.00',
    bid_increment: '1',
    duration_seconds: '30',  // Default to 30 sec for quick sprints
    stream_url: '',
    max_shipping_fee: '',
    timer_mode: 'sudden_death',
    // Quick-List fields
    quick_title: '',
    quick_description: '',
    // Giveaway mode
    is_giveaway: false
  });

  useEffect(() => {
    const fetchListings = async () => {
      try {
        const res = await api().get('/cards/user/listings');
        setUserListings(res.data.listings?.filter(l => l.status === 'active') || []);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    fetchListings();
  }, [api]);

  const handleCreate = async () => {
    // Validation
    if (auctionMode === 'regular' && !formData.card_id) {
      toast.error('Please select a card');
      return;
    }
    if (auctionMode === 'quicklist' && !formData.quick_title.trim()) {
      toast.error('Please enter a title for your Quick-List item');
      return;
    }
    
    setCreating(true);
    try {
      const payload = {
        starting_price: formData.is_giveaway ? 0 : parseFloat(formData.starting_price),
        bid_increment: parseFloat(formData.bid_increment),
        duration_seconds: parseInt(formData.duration_seconds),
        stream_url: formData.stream_url || null,
        max_shipping_fee: formData.max_shipping_fee ? parseFloat(formData.max_shipping_fee) : null,
        timer_mode: formData.is_giveaway ? 'sudden_death' : formData.timer_mode,
        session_id: sessionId,
        is_giveaway: formData.is_giveaway
      };
      
      if (auctionMode === 'quicklist') {
        payload.is_temporary = true;
        payload.quick_title = formData.quick_title.trim();
        payload.quick_description = formData.quick_description.trim() || null;
      } else {
        payload.card_id = formData.card_id;
        payload.is_temporary = false;
      }
      
      const res = await api().post('/auctions', payload);
      
      const successMsg = formData.is_giveaway 
        ? '🎁 Giveaway started!'
        : (auctionMode === 'quicklist' ? 'Quick-List auction created!' : 'Auction created! You\'re live!');
      toast.success(successMsg);
      onCreated(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create auction');
    }
    setCreating(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Radio className="w-5 h-5 text-red-500" />
            Go Live
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="py-8 text-center">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            {/* Mode Selection Tabs */}
            <Tabs value={auctionMode} onValueChange={setAuctionMode} className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-zinc-800">
                <TabsTrigger value="regular" className="data-[state=active]:bg-[#00BFFF] data-[state=active]:text-black">
                  <Gavel className="w-4 h-4 mr-2" />
                  Regular
                </TabsTrigger>
                <TabsTrigger value="quicklist" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
                  <Bolt className="w-4 h-4 mr-2" />
                  Quick-List
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="regular" className="space-y-4 mt-4">
                {/* Card Selection */}
                <div>
                  <Label className="text-white mb-2 block">Select Card to Auction</Label>
                  <Select value={formData.card_id} onValueChange={(v) => setFormData({...formData, card_id: v})}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue placeholder="Choose a listing" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {userListings.map(listing => (
                        <SelectItem key={listing.id} value={listing.id}>
                          {listing.player_name} - ${listing.price}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {userListings.length === 0 && (
                    <p className="text-zinc-500 text-sm mt-2">No active listings. <Link to="/sell" className="text-[#00BFFF]">Create one first</Link>.</p>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="quicklist" className="space-y-4 mt-4">
                {/* Preset Buttons Row */}
                <div className="flex gap-3">
                {/* Dollar Sprint Preset Button */}
                <button
                  type="button"
                  onClick={() => setFormData({
                    ...formData,
                    starting_price: '1.00',
                    duration_seconds: '5',
                    timer_mode: 'sudden_death',
                    is_giveaway: false
                  })}
                  className={`flex-1 p-4 rounded-xl transition-all ${
                    !formData.is_giveaway 
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400' 
                      : 'bg-zinc-800 border border-zinc-700 hover:border-amber-500/50'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Bolt className={`w-5 h-5 ${!formData.is_giveaway ? 'text-white' : 'text-amber-400'}`} />
                    <div className="text-left">
                      <p className={`font-bold ${!formData.is_giveaway ? 'text-white' : 'text-amber-400'}`}>Dollar Sprint</p>
                      <p className={`text-xs ${!formData.is_giveaway ? 'text-white/80' : 'text-zinc-500'}`}>$1 • 5s</p>
                    </div>
                  </div>
                </button>

                {/* Givvy (Giveaway) Preset Button */}
                <button
                  type="button"
                  onClick={() => setFormData({
                    ...formData,
                    starting_price: '0',
                    duration_seconds: '30',
                    timer_mode: 'sudden_death',
                    is_giveaway: true
                  })}
                  className={`flex-1 p-4 rounded-xl transition-all ${
                    formData.is_giveaway 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400' 
                      : 'bg-zinc-800 border border-zinc-700 hover:border-purple-500/50'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Trophy className={`w-5 h-5 ${formData.is_giveaway ? 'text-white' : 'text-purple-400'}`} />
                    <div className="text-left">
                      <p className={`font-bold ${formData.is_giveaway ? 'text-white' : 'text-purple-400'}`}>Givvy</p>
                      <p className={`text-xs ${formData.is_giveaway ? 'text-white/80' : 'text-zinc-500'}`}>FREE • 30s</p>
                    </div>
                  </div>
                </button>
                </div>

                {/* Giveaway Info Banner (when Givvy mode selected) */}
                {formData.is_giveaway && (
                  <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <p className="text-purple-400 text-sm flex items-center gap-2">
                      <Trophy className="w-4 h-4" />
                      <span className="font-medium">🎁 Giveaway Mode</span>
                    </p>
                    <p className="text-zinc-400 text-xs mt-1">
                      Random winner selected at 0:00! Users join with verified address. Winner gets FREE shipping!
                    </p>
                  </div>
                )}

                {/* Quick-List Info Banner */}
                {!formData.is_giveaway && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-amber-400 text-sm flex items-center gap-2">
                    <Bolt className="w-4 h-4" />
                    <span className="font-medium">Quick-List Mode</span>
                  </p>
                  <p className="text-zinc-400 text-xs mt-1">
                    Create a temporary auction without a full listing. Perfect for bulk items you're showing live on camera. No image required!
                  </p>
                </div>
                )}
                
                {/* Quick Title */}
                <div>
                  <Label className="text-white mb-2 block">Item Title *</Label>
                  <Input 
                    placeholder="e.g., Vintage Bulk Lot, Holo Pack, Mystery Box"
                    value={formData.quick_title}
                    onChange={(e) => setFormData({...formData, quick_title: e.target.value})}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                
                {/* Quick Description (Optional) */}
                <div>
                  <Label className="text-white mb-2 block">Description (Optional)</Label>
                  <Input 
                    placeholder="Brief description of the item"
                    value={formData.quick_description}
                    onChange={(e) => setFormData({...formData, quick_description: e.target.value})}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                
                {/* Camera Badge */}
                <div className="flex items-center gap-2 text-zinc-400 text-sm">
                  <Camera className="w-4 h-4" />
                  <span>Show item on camera during auction</span>
                </div>
              </TabsContent>
            </Tabs>

            {/* Starting Price */}
            <div>
              <Label className="text-white mb-2 block">Starting Price</Label>
              <Input 
                type="number"
                step="0.01"
                value={formData.starting_price}
                onChange={(e) => setFormData({...formData, starting_price: e.target.value})}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>

            {/* Bid Increment */}
            <div>
              <Label className="text-white mb-2 block">Bid Increment</Label>
              <Select value={formData.bid_increment} onValueChange={(v) => setFormData({...formData, bid_increment: v})}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="1">$1</SelectItem>
                  <SelectItem value="5">$5</SelectItem>
                  <SelectItem value="10">$10</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Duration */}
            <div>
              <Label className="text-white mb-2 block flex items-center gap-2">
                <Timer className="w-4 h-4" />
                Auction Duration
              </Label>
              <Select value={formData.duration_seconds} onValueChange={(v) => setFormData({...formData, duration_seconds: v})}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                  {TIMER_INTERVALS.map(timer => (
                    <SelectItem key={timer.value} value={timer.value}>
                      <span className={timer.category === 'sprint' ? 'text-amber-400' : ''}>
                        {timer.category === 'sprint' ? '⚡ ' : ''}{timer.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-zinc-500 text-xs mt-1">
                {parseInt(formData.duration_seconds) <= 45 
                  ? "⚡ Sprint mode - perfect for Dollar Sprints & pack breaks!"
                  : "Standard auction duration"
                }
              </p>
            </div>

            {/* Timer Mode */}
            <div>
              <Label className="text-white mb-2 block flex items-center gap-2">
                <Timer className="w-4 h-4" />
                Timer Mode
              </Label>
              <Select value={formData.timer_mode} onValueChange={(v) => setFormData({...formData, timer_mode: v})}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="sudden_death">
                    <div className="flex items-center gap-2">
                      <Flame className="w-4 h-4 text-red-500" />
                      <span>Sudden Death</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="extension">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-amber-500" />
                      <span>Extension Auction</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-zinc-500 text-xs mt-1">
                {formData.timer_mode === 'sudden_death' 
                  ? "Hard stop - timer ends exactly when it hits 0, no extensions."
                  : "Adds 5 seconds when a bid is placed in the final 5 seconds."
                }
              </p>
            </div>

            {/* Stream URL (Optional) */}
            <div>
              <Label className="text-white mb-2 block">Stream URL (Optional)</Label>
              <Input 
                placeholder="Twitch or YouTube embed URL"
                value={formData.stream_url}
                onChange={(e) => setFormData({...formData, stream_url: e.target.value})}
                className="bg-zinc-800 border-zinc-700"
              />
              <p className="text-zinc-500 text-xs mt-1">Paste your Twitch/YouTube embed link</p>
            </div>

            {/* Max Shipping Fee (Shipping Cap) */}
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <Label className="text-amber-400 mb-2 block flex items-center gap-2">
                <Package className="w-4 h-4" />
                Shipping Cap (Optional)
              </Label>
              <Input 
                type="number"
                step="0.01"
                min="0"
                placeholder="e.g., 12.00"
                value={formData.max_shipping_fee}
                onChange={(e) => setFormData({...formData, max_shipping_fee: e.target.value})}
                className="bg-zinc-800 border-zinc-700"
              />
              <p className="text-zinc-400 text-xs mt-2">
                Set a max shipping fee for this show. Once a buyer reaches this cap, all future wins ship FREE! 
                <span className="text-amber-400"> Leave blank for standard bundling ($5 base + $1/card).</span>
              </p>
            </div>

            <Button 
              onClick={handleCreate}
              disabled={creating || (auctionMode === 'regular' ? !formData.card_id : !formData.quick_title.trim())}
              className="w-full h-12 bg-red-600 hover:bg-red-500 text-white font-bold"
            >
              {creating ? <LoadingSpinner size="sm" /> : (
                <>
                  <Radio className="w-5 h-5 mr-2" />
                  Start Live Auction
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

function AuctionListPage() {
  const { api, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Check if user is Elite
  const isElite = user?.membership_status?.toLowerCase() === 'elite' || 
                  user?.membership_status?.toLowerCase() === 'creator' ||
                  user?.is_founder;

  const fetchAuctions = async () => {
    try {
      const res = await api().get('/auctions/live');
      setAuctions(res.data.auctions || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAuctions();
    // Poll every 10 seconds
    const interval = setInterval(fetchAuctions, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleAuctionCreated = (newAuction) => {
    setAuctions([newAuction, ...auctions]);
    navigate(`/auction/${newAuction.id}`);
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      
      {showCreateModal && (
        <CreateAuctionModal 
          onClose={() => setShowCreateModal(false)}
          onCreated={handleAuctionCreated}
        />
      )}

      <div className="pt-24 px-4 max-w-7xl mx-auto pb-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Gavel className="w-8 h-8 text-[#00BFFF]" />
              Live Auctions
            </h1>
            <p className="text-zinc-400 mt-1">Bid on exclusive cards in real-time</p>
          </div>
          
          {isAuthenticated && isElite && (
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="bg-red-600 hover:bg-red-500 text-white font-bold"
            >
              <Radio className="w-5 h-5 mr-2" />
              Go Live
            </Button>
          )}
          
          {isAuthenticated && !isElite && (
            <div className="text-right">
              <p className="text-zinc-400 text-sm mb-1">Want to host auctions?</p>
              <Link to="/subscription">
                <Button variant="outline" className="border-amber-500 text-amber-500">
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade to Elite
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Live Auctions Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : auctions.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {auctions.map(auction => (
              <AuctionCard key={auction.id} auction={auction} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Gavel className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">No Live Auctions</h2>
            <p className="text-zinc-400 mb-6">Check back soon or start your own!</p>
            {isElite && (
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="bg-red-600 hover:bg-red-500 text-white"
              >
                <Radio className="w-5 h-5 mr-2" />
                Go Live Now
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AuctionListPage;
